import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { aiEventTypeApi, aiScheduleApi, aiSystemApi, alertApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  EventFilterFields,
  EventTypeAutocomplete,
  buildEventFilters,
  createEmptyEventFilterValue,
  eventTypeAccessErrorMessage,
  eventFilterValueFromFilters,
  getAccessibleEventTypes,
  type EventFilterFormValue,
} from './EventTypeAutocomplete';
import type { AISchedule, AIScheduleCreate, ScheduleType, RecurringUnit } from '../../types';

interface ScheduleFormProps {
  open: boolean;
  onClose: () => void;
  schedule?: AISchedule;
}

export const ScheduleForm: React.FC<ScheduleFormProps> = ({ open, onClose, schedule }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.is_staff === true || user?.is_superuser === true;
  const isEditing = !!schedule;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [eventFilters, setEventFilters] = useState<EventFilterFormValue>(
    createEmptyEventFilterValue()
  );

  const [formData, setFormData] = useState<AIScheduleCreate>({
    ai_system: 0,
    type: 'ONE_TIME',
    start_at: null,
    end_at: null,
    interval_every: null,
    interval_period: null,
    excluded_days: null,
    is_active: true,
  });

  const [error, setError] = useState<string | null>(null);

  // Fetch AI systems for dropdown
  const { data: systemsData } = useQuery({
    queryKey: ['ai-systems'],
    queryFn: () => aiSystemApi.list({}),
  });

  const {
    data: eventTypes = [],
    error: eventTypesError,
    isFetching: isEventTypesLoading,
  } = useQuery({
    queryKey: ['ai-event-types', { includeAdmin: isAdmin }],
    queryFn: () => aiEventTypeApi.list({ includeAdmin: isAdmin }),
    enabled: open && !!user,
  });
  const eventTypesErrorMessage = eventTypeAccessErrorMessage(eventTypesError);
  const accessibleEventTypes = useMemo(
    () => getAccessibleEventTypes(eventTypes, isAdmin),
    [eventTypes, isAdmin]
  );
  const selectedEventType = formData.event_config?.event_type || '';
  const selectedEventTypeDefinition =
    accessibleEventTypes.find((eventType) => eventType.value === selectedEventType) || null;
  const selectedEventTypeIsAccessible =
    formData.type !== 'EVENT' ||
    selectedEventTypeDefinition !== null;
  const selectedEventRequiresAlert =
    selectedEventTypeDefinition?.value === 'alerts.triggered' &&
    !eventFilters.payloadFields.alert_id;
  const { data: alerts = [], isFetching: isAlertsLoading } = useQuery({
    queryKey: ['alerts', 'event-schedule-picker'],
    queryFn: () => alertApi.list(),
    enabled: open && selectedEventTypeDefinition?.value === 'alerts.triggered',
  });

  useEffect(() => {
    if (schedule) {
      console.log('Loading schedule for editing:', schedule);
      console.log('interval_every:', schedule.interval_every);
      console.log('interval_period:', schedule.interval_period);
      const triggerFilters = schedule.event_trigger?.filters || {};
      
      setFormData({
        ai_system: schedule.ai_system,
        type: schedule.type,
        start_at: schedule.start_at,
        end_at: schedule.end_at,
        interval_every: schedule.interval_every,
        interval_period: schedule.interval_period,
        excluded_days: schedule.excluded_days,
        event_config: schedule.event_trigger
          ? {
              event_type: schedule.event_trigger.event_type,
              filters: schedule.event_trigger.filters || {},
              conditions: schedule.event_trigger.conditions || [],
              cooldown_seconds: schedule.event_trigger.cooldown_seconds || 0,
              dedupe_window_seconds: schedule.event_trigger.dedupe_window_seconds || 60,
              while_running_policy: schedule.event_trigger.while_running_policy || 'drop',
              is_active: schedule.event_trigger.is_active,
            }
          : null,
        is_active: schedule.is_active,
      });
      setEventFilters(eventFilterValueFromFilters(triggerFilters));
    } else {
      setFormData({
        ai_system: 0,
        type: 'ONE_TIME',
        start_at: null,
        end_at: null,
        interval_every: null,
        interval_period: null,
        excluded_days: null,
        event_config: null,
        is_active: true,
      });
      setEventFilters(createEmptyEventFilterValue());
    }
    setError(null);
  }, [schedule, open]);

  const handleChange = (field: keyof AIScheduleCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: (data: AIScheduleCreate) => aiScheduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create schedule');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: AIScheduleCreate) => aiScheduleApi.update(schedule!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update schedule');
    },
  });

  const handleSubmit = () => {
    if (!formData.ai_system) {
      setError('Please select an AI system');
      return;
    }

    if (formData.type === 'ONE_TIME' && !formData.start_at) {
      setError('Please specify a start time for one-time schedules');
      return;
    }

    if (formData.type === 'RECURRING' && (!formData.interval_every || !formData.interval_period)) {
      setError('Please specify interval for recurring schedules');
      return;
    }

    if (formData.type === 'EVENT' && eventTypesErrorMessage) {
      setError(eventTypesErrorMessage);
      return;
    }

    if (formData.type === 'EVENT' && (!selectedEventTypeIsAccessible || selectedEventRequiresAlert)) {
      setError(selectedEventRequiresAlert ? 'Please select an alert' : 'Please select an event type');
      return;
    }

    const submitData: AIScheduleCreate = { ...formData };
    if (submitData.type === 'EVENT') {
      submitData.event_config = {
        ...submitData.event_config!,
        filters: buildEventFilters(selectedEventTypeDefinition, eventFilters),
        conditions: submitData.event_config?.conditions || [],
        cooldown_seconds: submitData.event_config?.cooldown_seconds || 0,
        dedupe_window_seconds: submitData.event_config?.dedupe_window_seconds || 60,
        while_running_policy: submitData.event_config?.while_running_policy || 'drop',
        is_active: true,
      };
      submitData.interval_every = null;
      submitData.interval_period = null;
      submitData.excluded_days = null;
      submitData.excluded_time_ranges = null;
    }

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "sm"} 
      fullWidth 
      fullScreen={isMobile || isFullscreen}
    >
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            {isEditing ? 'Edit Schedule' : 'Create Schedule'}
          </Typography>
          {!isMobile && (
            <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              select
              label="AI System"
              value={formData.ai_system}
              onChange={(e) => handleChange('ai_system', parseInt(e.target.value))}
              required
              disabled={isLoading}
              InputProps={{ style: { fontSize: 16 } }}
            >
              <MenuItem value={0}>Select AI System</MenuItem>
              {systemsData?.results.map((system) => (
                <MenuItem key={system.id} value={system.id}>
                  {system.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              select
              label="Schedule Type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value as ScheduleType)}
              required
              disabled={isLoading}
            >
              <MenuItem value="ONE_TIME">One Time</MenuItem>
              <MenuItem value="RECURRING">Recurring</MenuItem>
              <MenuItem value="EVENT">Event Trigger</MenuItem>
            </TextField>
          </Grid>

          {formData.type === 'ONE_TIME' && (
            <>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start At"
                  value={formData.start_at ? new Date(formData.start_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Convert local datetime to ISO string with proper timezone
                      const localDate = new Date(e.target.value);
                      handleChange('start_at', localDate.toISOString());
                    } else {
                      handleChange('start_at', null);
                    }
                  }}
                  required
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  helperText="When to run the schedule (your local time)"
                />
              </Grid>
            </>
          )}

          {formData.type === 'RECURRING' && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recurrence Pattern
                </Typography>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Every"
                    type="number"
                    value={formData.interval_every || ''}
                    onChange={(e) => handleChange('interval_every', parseInt(e.target.value) || null)}
                    required
                    disabled={isLoading}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    label="Period"
                    value={formData.interval_period || ''}
                    onChange={(e) => handleChange('interval_period', e.target.value as RecurringUnit || null)}
                    required
                    disabled={isLoading}
                    sx={{ flex: 2 }}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                  >
                    <MenuItem value="">Select Period</MenuItem>
                    <MenuItem value="seconds">Seconds</MenuItem>
                    <MenuItem value="minutes">Minutes</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                  </TextField>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start At (Optional)"
                  value={formData.start_at ? new Date(formData.start_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Convert local datetime to ISO string with proper timezone
                      const localDate = new Date(e.target.value);
                      handleChange('start_at', localDate.toISOString());
                    } else {
                      handleChange('start_at', null);
                    }
                  }}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  helperText="When to start the recurring schedule (your local time)"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="End At (Optional)"
                  value={formData.end_at ? new Date(formData.end_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Convert local datetime to ISO string with proper timezone
                      const localDate = new Date(e.target.value);
                      handleChange('end_at', localDate.toISOString());
                    } else {
                      handleChange('end_at', null);
                    }
                  }}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  helperText="When to stop the recurring schedule (your local time)"
                />
              </Grid>
            </>
          )}

          {formData.type === 'EVENT' && (
            <>
              <Grid size={{ xs: 12 }}>
                <EventTypeAutocomplete
                  eventTypes={eventTypes}
                  value={selectedEventType}
                  onChange={(eventType) => {
                    setEventFilters(createEmptyEventFilterValue());
                    handleChange('event_config', {
                      ...(formData.event_config || {}),
                      event_type: eventType,
                    });
                  }}
                  required
                  disabled={isLoading}
                  loading={isEventTypesLoading}
                  accessError={eventTypesErrorMessage}
                  isAdmin={isAdmin}
                />
                {selectedEventType && !selectedEventTypeIsAccessible && !isEventTypesLoading && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Selected event type is unavailable to this account. Choose an accessible event type.
                  </Alert>
                )}
              </Grid>
              <EventFilterFields
                eventType={selectedEventTypeDefinition}
                value={eventFilters}
                onChange={setEventFilters}
                disabled={isLoading}
                alerts={alerts}
                alertsLoading={isAlertsLoading}
              />
              {selectedEventRequiresAlert && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    Select the alert that should trigger this AI system.
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cooldown Seconds"
                  value={formData.event_config?.cooldown_seconds || 0}
                  onChange={(e) =>
                    handleChange('event_config', {
                      ...(formData.event_config || {}),
                      cooldown_seconds: parseInt(e.target.value) || 0,
                    })
                  }
                  inputProps={{ min: 0 }}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dedupe Window Seconds"
                  value={formData.event_config?.dedupe_window_seconds || 60}
                  onChange={(e) =>
                    handleChange('event_config', {
                      ...(formData.event_config || {}),
                      dedupe_window_seconds: parseInt(e.target.value) || 60,
                    })
                  }
                  inputProps={{ min: 0 }}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="While Running"
                  value={formData.event_config?.while_running_policy || 'drop'}
                  onChange={(e) =>
                    handleChange('event_config', {
                      ...(formData.event_config || {}),
                      while_running_policy: e.target.value as 'drop' | 'queue',
                    })
                  }
                  disabled={isLoading}
                >
                  <MenuItem value="drop">Drop</MenuItem>
                  <MenuItem value="queue">Queue</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start At (Optional)"
                  value={formData.start_at ? new Date(formData.start_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const localDate = new Date(e.target.value);
                      handleChange('start_at', localDate.toISOString());
                    } else {
                      handleChange('start_at', null);
                    }
                  }}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="End At (Optional)"
                  value={formData.end_at ? new Date(formData.end_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const localDate = new Date(e.target.value);
                      handleChange('end_at', localDate.toISOString());
                    } else {
                      handleChange('end_at', null);
                    }
                  }}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > button': { width: isMobile ? '100%' : 'auto' }
      }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
